import { useEffect } from 'react'
import { useEventListener, useLocalStorage } from 'usehooks-ts'

import { Logger } from '@shared/utils'

const LIMIT = 50
const log = Logger('usePagesHistory')

const STORAGE_KEY = 'pages-history-v3'
const LEGACY_STORAGE_KEY = 'pages-history-v2'
const OBSOLETE_STORAGE_KEY = 'pages-history-v1'

export const PageHistoryType = {
  Patient: 'patient',
  Lab: 'lab',
  Rx: 'rx',
  Encounter: 'encounter',
}

const pickHistoryData = (type, data) => {
  if (!data) return data

  switch (type) {
    case PageHistoryType.Patient:
      return {
        id: data.id,
        fullName: data.fullName,
        firstName: data.firstName,
        lastName: data.lastName,
        profilePictureThumbUrl: data.profilePictureThumbUrl,
      }
    case PageHistoryType.Lab:
      return { id: data.id, lab: data.lab, status: data.status }
    case PageHistoryType.Rx:
      return { id: data.id, drugName: data.drugName, rxStatus: data.rxStatus }
    case PageHistoryType.Encounter:
      return { id: data.id, description: data.description }
    default:
      return data
  }
}

const migratePagesHistory = () => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(OBSOLETE_STORAGE_KEY)

    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!raw) return

    const legacy = JSON.parse(raw)
    const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]')

    const seen = new Set()
    const merged = []
    for (const item of [...existing, ...(Array.isArray(legacy) ? legacy : [])]) {
      if (item && !seen.has(item.id)) {
        seen.add(item.id)
        merged.push({ ...item, data: pickHistoryData(item.type, item.data) })
      }
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged.slice(0, LIMIT)))
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch (e) {
    // If anything fails, drop the legacy stores anyway so a corrupt or oversized
    // value can't wedge the migration (and re-run it) on every load.
    log.error('Failed to migrate pages history; dropping legacy stores:', e)
    window.localStorage.removeItem(OBSOLETE_STORAGE_KEY)
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  }
}

migratePagesHistory()

const freeUpSpaceForPagesHistory = (setHistory) => {
  let freed = false
  try {
    setHistory((prev) => {
      if (prev.length === 0) return prev
      // Remove the oldest item (last in the array)
      freed = true
      log.warn('Removing oldest history item to free up space')
      return prev.slice(0, -1)
    })
    return freed
  } catch (e) {
    log.error('Failed to free up space for pages history:', e)
    return false
  }
}

export const getPageHistoryId = (type, id) => `${type}-${id}`

export default function usePagesHistory() {
  const [history, setHistory] = useLocalStorage(STORAGE_KEY, [])

  const add = (type, data, parentId) => {
    const slimData = pickHistoryData(type, data)

    const attemptToAdd = () => {
      while (true) {
        try {
          setHistory((history) => {
            const id = getPageHistoryId(type, slimData.id)
            const createdAt = new Date().toISOString()

            const item = { id, createdAt, type, parentId, data: slimData }
            const updatedHistory = [item, ...history.filter((item) => item.id !== id)]

            if (updatedHistory.length > LIMIT) updatedHistory.pop()

            return updatedHistory
          })
          return true
        } catch (e) {
          if (e.name === 'QuotaExceededError') {
            log.warn('LocalStorage quota exceeded while adding to pages history')
            if (freeUpSpaceForPagesHistory(setHistory)) {
              log.warn('Retrying to add to pages history after freeing up space')
              continue
            }
            log.warn('Could not free up space for pages history')
          } else {
            log.error('Error adding to pages history:', e)
          }
          return false
        }
      }
    }

    attemptToAdd()
  }

  const clear = () => setHistory([])

  return { history, add, clear }
}

export function usePagesHistoryListener(type, data, parentId) {
  const { add } = usePagesHistory()

  useEffect(() => {
    if (type && data) {
      log.debug('Adding to pages history via useEffect', { type, data, parentId })
      add(type, data, parentId)
    }
  }, [data, parentId, type]) // eslint-disable-line react-hooks/exhaustive-deps

  // Add item to the history when returns back to the page
  useEventListener('visibilitychange', () => {
    if (!document.hidden) {
      log.debug('Adding to pages history via visibilitychange', { type, data, parentId })
      add(type, data, parentId)
    }
  })
}
